import React, { useCallback, useEffect, useReducer, useState } from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { isObject } from 'lodash-es';
import { useIntl } from 'react-intl';
import { View } from 'react-native';

import { IRestaurant } from '@rbi-ctg/store';
import { addressReducer } from 'components/address-modal/reducer';
import { CardModal } from 'components/card-modal';
import Dialog from 'components/dialog';
import {
  DeliveryStatus,
  DeliveryStoreStatus,
  useDeliveryRestaurantLazyQuery,
} from 'generated/graphql-gateway';
import usePlaceIdDetails from 'hooks/geolocation/use-place-id-details';
import useErrorModal from 'hooks/use-error-modal';
import { useDismissAndReplace } from 'navigation/hooks/use-dismiss-and-navigate';
import {
  DeliveryStoreStatusToStoreStatusMap,
  IGetClosestAvailableDeliveryRestaurantResult,
} from 'remote/api/restaurants';
import { CustomEventNames, EventTypes, logNavigationClick, logRBIEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ServiceMode, useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { convertMilesToMeters } from 'utils/distance';
import { isNativeIOS } from 'utils/environment';
import { platform } from 'utils/environment';
import { IPlaceData } from 'utils/geolocation';
import { routes } from 'utils/routing';

import { DeliveryAddresses } from './delivery-addresses';
import { DeliveryUnavailable } from './delivery-unavailable';
import { DeliveryAddressForm } from './new-address-form';
import { ActionStatus, SelectedDeliveryAddress } from './types';
import { getAddressLabel } from './utils';

export const DeliveryAddressModal = () => {
  const { formatMessage } = useIntl();

  const dismissAndReplace = useDismissAndReplace();

  const [state, dispatch] = useReducer(addressReducer, {
    error: null,
    address: null,
    status: ActionStatus.IDLE,
  });

  const header = formatMessage({
    id:
      state.status === ActionStatus.QUOTE_UNAVAILABLE
        ? 'deliveryNotAvailable'
        : 'deliverySelectYourAddress',
  });
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const {
    StoreChangeDialog,
    // FIXME: we need to remove these 3 lines after the refactor for the store locator
    orderingUnavailableOpen,
    onConfirmLocateRestaurants,
    onDismissStoreUnavailable,
  } = useStoreContext();

  const {
    user,
    saveDeliveryAddress,
    editDeliveryAddress,
    removeDeliveryAddress,
  } = useAuthContext();
  const [createAddressView, setCreateAddressView] = useState(false);
  const [isNewAddress, setIsNewAddress] = useState(false);
  const [
    queryDeliveryRestaurant,
    { data: deliveryRestaurantData, error: deliveryRestaurantError },
  ] = useDeliveryRestaurantLazyQuery({ fetchPolicy: 'no-cache' });
  const { setDeliverySurchargeFee } = useServiceModeContext();
  const getDetails = usePlaceIdDetails();

  const {
    setDeliveryAddress,
    setDeliveryInstructions,
    setOrderPhoneNumber,
    setQuoteId,
    selectStore,
    selectServiceMode,
    deliveryInstructions,
  } = useOrderContext();

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Delivery Not Completed',
  });

  const deliveryRadius = useFlag(LaunchDarklyFlag.DELIVERY_RADIUS_IN_MILES);
  const showDeliveryAddresses = Boolean(user?.details?.deliveryAddresses?.length);

  const handleQuoteResult = useCallback(
    async ({ store, deliveryQuote }: IGetClosestAvailableDeliveryRestaurantResult) => {
      // if store available, route to store
      if (store && deliveryQuote === DeliveryStatus.QUOTE_SUCCESSFUL) {
        dispatch({
          type: ActionStatus.SUCCESS,
        });

        return selectStore(
          store,
          async () => {
            if (state.address) {
              const {
                address,
                shouldSaveAddress,
                deliveryInstructions,
                phoneNumber,
                coordinates,
              } = state.address;

              setDeliveryAddress({
                ...address,
                shouldSave: shouldSaveAddress,
                latitude: coordinates?.lat ?? undefined,
                longitude: coordinates?.lng ?? undefined,
              });

              setDeliveryInstructions(deliveryInstructions);
              setOrderPhoneNumber(phoneNumber);
            }

            const serviceMode = ServiceMode.DELIVERY;
            selectServiceMode(serviceMode);

            dismissAndReplace(routes.menu);
          },
          ServiceMode.DELIVERY
        );
      }

      if (deliveryQuote === DeliveryStatus.QUOTE_UNAVAILABLE) {
        return dispatch({ type: ActionStatus.QUOTE_UNAVAILABLE });
      }

      if (deliveryQuote === DeliveryStatus.QUOTE_ERROR) {
        return dispatch({
          type: ActionStatus.ERROR,
          error: {
            message: formatMessage({ id: 'pleaseTryAgainLater' }),
            modalAppearanceEventMessage: 'Error: Delivery Quote Error',
          },
        });
      }
    },
    [
      formatMessage,
      selectServiceMode,
      selectStore,
      setDeliveryAddress,
      setDeliveryInstructions,
      setOrderPhoneNumber,
      state.address,
    ]
  );

  useEffect(() => {
    const restaurant = deliveryRestaurantData?.deliveryRestaurant?.restaurant;
    if (!restaurant) {
      return;
    }

    logRBIEvent({
      name: 'Delivery Store Assigned',
      type: EventTypes.Other,
      attributes: {
        restaurantId: restaurant._id || '',
        storeId: restaurant.storeId || '',
        restaurantAddress: restaurant.physicalAddress?.address1 || '',
        restaurantZip: restaurant.physicalAddress?.postalCode || '',
        restaurantCity: restaurant.physicalAddress?.city || '',
        restaurantState: restaurant.physicalAddress?.stateProvince || '',
        restaurantCountry: restaurant.physicalAddress?.country || '',
      },
    });
  }, [deliveryRestaurantData]);

  useEffect(() => {
    if (deliveryRestaurantData?.deliveryRestaurant) {
      const {
        quote,
        restaurant,
        storeStatus,
        nextEarliestOpen,
        deliverySurchargeFeeCents,
        quoteId,
      } = deliveryRestaurantData?.deliveryRestaurant;

      if (deliverySurchargeFeeCents) {
        setDeliverySurchargeFee(deliverySurchargeFeeCents);
      }

      setQuoteId(quoteId || '');

      handleQuoteResult({
        // @ts-expect-error TS(2322) FIXME: Type 'DeliveryStatus | null | undefined' is not as... Remove this comment to see the full error message
        deliveryQuote: quote,
        store: restaurant as IRestaurant,
        storeStatus: DeliveryStoreStatusToStoreStatusMap[storeStatus],
        nextEarliestOpen: nextEarliestOpen ? new Date(nextEarliestOpen) : undefined,
      });
    }

    /*
     * TODO: `handleQuoteResult` is a created by `useCallback`. It's updated
     * whenever `selectServiceMode` is updated, which is also created by
     * `useCallback`. `selectServiceMode` is updated whenever the `serviceMode`
     * state variable is changed. `selectServiceMode` is called in
     * `handleQuoteResult` causing an infinite render loop.
     *
     * Leaving `handleQuoteResult` out of the dependency array solves the issue
     * for now, but might needs a closer look as we organize state more effectively.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryRestaurantData]);

  const completeDelivery = async (data: SelectedDeliveryAddress, savedAddress = false) => {
    if (state.status === ActionStatus.PENDING) {
      return;
    }

    if (!data.address) {
      return dispatch({
        type: ActionStatus.ERROR,
        error: { message: 'Missing address information for delivery' },
      });
    }

    const {
      address,
      phoneNumber,
      coordinates: selectedCoordinates,
      placeId: selectedPlaceId,
    } = data;

    dispatch({ type: ActionStatus.PENDING, address: data });

    try {
      const placeId = selectedPlaceId;

      // TODO: RN - In very rare situations you could end up here without a placeId. Add an Address to PlaceId lookup back
      // if (!selectedCoordinates && !selectedPlaceId) {
      //   // only get the placeId if we need it to look up coordinates
      //   placeId = await getPlaceId(getAddressLabel(data));
      // }

      // TODO: RN - Is this extra place lookup really needed?
      const { coordinates } = selectedCoordinates
        ? { coordinates: selectedCoordinates }
        : await getDetailsPlaceData(placeId);

      if (!coordinates) {
        throw new Error('Unable to get coordinates from selected address');
      }

      setIsNewAddress(!savedAddress);

      queryDeliveryRestaurant({
        variables: {
          dropoff: {
            ...address,
            latitude: coordinates.lat,
            longitude: coordinates.lng,
            phoneNumber,
          },
          searchRadius: convertMilesToMeters(deliveryRadius),
          platform: platform(),
        },
      });
    } catch (error) {
      dispatch({
        type: ActionStatus.ERROR,
        error: { message: 'Error using delivery address', error: error as any },
      });
    }
  };

  useEffect(() => {
    if (!deliveryRestaurantData || deliveryRestaurantError) {
      return;
    }

    const storeId = deliveryRestaurantData?.deliveryRestaurant.restaurant?.storeId;

    let failureReason;
    if (
      deliveryRestaurantData?.deliveryRestaurant.storeStatus === DeliveryStoreStatus.NO_DELIVERY
    ) {
      failureReason = 'delivery unavailable';
    } else if (deliveryRestaurantError) {
      failureReason = 'Delivery restaurant API fail';
    } else if (!storeId) {
      failureReason = 'Restaurant not found';
    }

    logRBIEvent(
      {
        name: CustomEventNames.DELIVERY_ADDRESS_SUBMITTED,
        type: EventTypes.Other,
        attributes: {
          storeId: storeId || undefined,
          status: failureReason ? 'fail' : 'success',
          failureReason,
          searchType: isNewAddress ? 'newAddress' : 'savedAddress',
        },
      },
      { skipLoggingToBraze: true }
    );
  }, [deliveryRestaurantData, deliveryRestaurantError]);

  const getDetailsPlaceData = useCallback(
    (pId: string | undefined): Promise<IPlaceData> => {
      return getDetails(pId).then(placeData => {
        if (placeData) {
          return Promise.resolve(placeData);
        }

        logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE, {
          error: 'Address Not Recognized',
        });

        return Promise.reject(placeData);
      });
    },
    [getDetails]
  );

  useEffect(() => {
    if (state.status === ActionStatus.ERROR && state.error) {
      openErrorDialog(state.error);
    }
  }, [openErrorDialog, state.error, state.status]);

  const showAddresses = showDeliveryAddresses && !createAddressView;

  // to avoid flatlists nested in a scrollview or else react-native complains
  const Container = showAddresses ? ScrollView : View;

  return (
    <CardModal headerText={header} analyticsEvent={'Select your delivery address'}>
      <Container style={{ flex: 1 }} keyboardShouldPersistTaps="handled" testID="delivery-modal">
        {state.status === ActionStatus.QUOTE_UNAVAILABLE ? (
          <DeliveryUnavailable
            address={state.address ? getAddressLabel(state.address) : ''}
            close={() => dispatch({ type: ActionStatus.IDLE })}
          />
        ) : (
          <>
            {showAddresses ? (
              <DeliveryAddresses
                addresses={user?.details?.deliveryAddresses || []}
                onCreate={() => setCreateAddressView(true)}
                setAddress={address => completeDelivery(address, true)}
                isLoading={state.status === ActionStatus.PENDING}
                saveDeliveryAddress={saveDeliveryAddress}
                editDeliveryAddress={editDeliveryAddress}
                removeDeliveryAddress={removeDeliveryAddress}
              />
            ) : (
              <DeliveryAddressForm
                instructions={deliveryInstructions}
                getDetailsPlaceData={getDetailsPlaceData}
                setCreateAddressView={setCreateAddressView}
                showDeliveryAddresses={showDeliveryAddresses}
                user={user}
                setAddress={completeDelivery}
                isLoading={state.status === ActionStatus.PENDING}
              />
            )}
          </>
        )}

        <ErrorDialog testID="address-modal-error-dialog" />
        <StoreChangeDialog heading={formatMessage({ id: 'changeStores' })} />
        {/* all this dialog is a hack to temporary solve the issue that we have a modal
      inside a modal is not showed on IOS, this is happening just on IOS and seems to
      be related on how ios manage the modals https://rbictg.atlassian.net/browse/BKPE-4595 */}
        {isNativeIOS() && orderingUnavailableOpen && (
          <Dialog
            showDialog
            heading={
              isObject(orderingUnavailableOpen) && orderingUnavailableOpen.headingOverride
                ? orderingUnavailableOpen.headingOverride
                : formatMessage({ id: 'storeClosed' })
            }
            body={
              isObject(orderingUnavailableOpen) && orderingUnavailableOpen?.bodyOverride
                ? orderingUnavailableOpen?.bodyOverride
                : formatMessage({
                    id: enableOrdering ? 'cannotPlaceOrder' : 'closeMessageToFindNearbyRestaurants',
                  })
            }
            confirmButtonLabel={formatMessage({ id: 'findRestaurants' })}
            confirmDialog={onConfirmLocateRestaurants}
            onDismiss={onDismissStoreUnavailable}
            modalAppearanceEventMessage="Ordering is unavailable"
            isErrorModal
          />
        )}
      </Container>
    </CardModal>
  );
};
